import type { GetUserType } from '@orbit/api-types'
import React from 'react'
import { getInitials } from '@/utils/userNames'
import { AvatarImage, AvatarRoot, type AvatarSize, type AvatarTone } from './Avatar.styles'

export interface AvatarProps {
	/** Whose avatar: their picture when there is one, otherwise their initials. */
	user?: Pick<GetUserType, 'firstName' | 'lastName' | 'profilePicture'> | null
	/** A picture URL when the caller has one but no user object. */
	src?: string | null
	/** Text instead of initials — "+3" on an overflow bubble, a single letter. */
	label?: string
	size?: AvatarSize
	tone?: AvatarTone
	/** A 2px white ring, for overlapping stacks. */
	ring?: boolean
	/** Shown when there is neither a picture nor text — an icon, say. */
	fallback?: React.ReactNode
	className?: string
	title?: string
}

/**
 * The one avatar: a circle carrying the user's picture or their initials in
 * Lato 700, in four sizes and four tones. Stacks overlap it through a
 * `styled(Avatar)` wrapper that sets the negative margin; the ring keeps the
 * edges readable.
 */
export const Avatar: React.FC<AvatarProps> = ({ user, src, label, size = 'md', tone = 'accent', ring = false, fallback, className, title }) => {
	const picture = src ?? user?.profilePicture
	const initials = label ?? (user ? getInitials(user) : undefined)
	return (
		<AvatarRoot $size={size} $tone={tone} $ring={ring} className={className} title={title}>
			{picture ? <AvatarImage src={picture} alt={initials ?? ''} /> : (initials ?? fallback)}
		</AvatarRoot>
	)
}

export default Avatar
